// @flow
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import GisLineEditor from 'esm-web-pro/containers/pages/network/objects/gisline/GisLineEditor';
import { get } from 'lodash';
import * as React from 'react';
import type { GISLine } from 'esm-web-pro/containers/pages/network/objects/gisline/GisLine';
import { injectIntl, IntlShape, defineMessages } from 'react-intl';
import { withRouter } from 'react-router';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI, withConfirmation } from 'speed-js-react';
import type { Confirmation } from 'speed-js-react';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { connect } from 'react-redux';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, skipSensitivity: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    line: GISLine,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    history: Object,
    match: Object,
    map: Object,
    deleteObject?: Object,
    navigateBack: Function,
    editor: Object,
    updateObject: (data: Object) => void,
    onView: Function,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-line-title',
        defaultMessage: 'Are you sure you want to delete the line?',
    },
    cancelDelete: {
        id: 'delete-line-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-line-confirm',
        defaultMessage: 'Confirm',
    },
    cantDeleteCables: {
        id: 'cant-delete-line-cables',
        defaultMessage: 'The line cannot be deleted because it contains pipes with at least one cable',
    },
    cantDeleteContinuousPipes: {
        id: 'cant-delete-line-continuous-pipes',
        defaultMessage: 'The line cannot be deleted because it contains continuous pipes.',
    },
    title: {
        id: 'edit-gisline-title',
        defaultMessage: 'Edit Gisline',
    },
});

const GIS_LINE_EDIT_KEY = 'GIS_LINE_EDIT_KEY';

const GisLineActions = (props: Props) => {
    const {
        line, map: { is_draft: isDraft, uuid: mapUuid }, confirm, intl: { formatMessage }, deleteObject,
        navigateBack, editor, updateObject, map, onView, history, openPanel, closePanel, match,
    } = props;

    const actions = [];

    const handleClose = (skipSensitivity = false) => {
        closePanel(GIS_LINE_EDIT_KEY, skipSensitivity);
    };

    const handleSave = (data: Object) => {
        updateObject(data);
        handleClose(true);
    };

    const handleDeleted = () => {
        handleClose();
        navigateBack();
    };

    const disallowDeleteConfirm = (message) => confirm({
        message,
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {
                },
                type: 'negative',
            },
        },
    });

    const allowDeleteConfirm = () => confirm({
        message: formatMessage(msg.deleteTitle),
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {
                },
                type: 'negative',
            },
            confirm: {
                label: formatMessage(msg.confirmDelete),
                callback: () => {
                    if (deleteObject) {
                        deleteObject(line.identity, 'gisLine.geo', [mapUuid])
                            .then((res) => {
                                if (res.data && res.data.error) {
                                    disallowDeleteConfirm(res.data.error);
                                } else {
                                    handleDeleted();
                                }
                            });
                    }
                },
                type: 'positive',
            },
        },
    });

    const deleteLine = () => {
        if (line.pipes.find((pipe) => pipe.cables.length > 0)) {
            disallowDeleteConfirm(formatMessage(msg.cantDeleteCables));
        } else if (line.pipes.find((pipe) => pipe.continuous_pipe)) {
            disallowDeleteConfirm(formatMessage(msg.cantDeleteContinuousPipes));
        } else {
            allowDeleteConfirm();
        }
    };

    if (isDraft) {
        actions.push(
            {
                key: `${line.uuid}-edit`,
                icon: <EditIcon size="small" />,
                callback: () => {
                    openPanel(GIS_LINE_EDIT_KEY, '', match.url, true);
                    onView();
                },
                tooltip: formatMessage(actionMessages.edit),
                disabled: editor.state.loading,
                marginVertical: '15px',
                radius: '0%',
                color: 'transparent',
            },
        );
    }

    actions.push({
        icon: <TimelineIcon />,
        callback: () => {
            onView();
            props.history.push(`${props.match.url}/timeline/gisline/${line.identity}`.replace('//', '/'));
        },
        tooltip: formatMessage(actionMessages.timeline),
        marginVertical: '15px',
        radius: '0%',
        color: 'transparent',
    });

    if (isDraft) {
        actions.push({
            key: `${line.uuid}-delete`,
            icon: <DeleteIcon size="small" />,
            callback: () => deleteLine(),
            tooltip: formatMessage(actionMessages.delete),
            disabled: editor.state.loading,
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    return (
        <>
            <SidePane
                id={GIS_LINE_EDIT_KEY}
                /* TODO: Add GisLineEditor title */
                title={`${formatMessage(msg.title)} ${line.ref}`}
            >
                <GisLineEditor
                    id={GIS_LINE_EDIT_KEY}
                    line={line}
                    onClose={handleClose}
                    onSave={handleSave}
                    map={map}
                    editor={editor}
                    history={history}
                    match={match}
                />
            </SidePane>
            <MainActions main actions={actions} />
        </>
    );
};

GisLineActions.defaultProps = {
    deleteObject: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.delete(...resourceArguments, id);
        },
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(GisLineActions),
            ),
        ),
    ),
);
